<template>
  <div class="dg reset-password-page">
    <header class="reset-password-header">
      <img
        alt="Rallyhood Logo"
        class="logo"
        src="/rallyhood-white.svg"
      >
    </header>

    <main class="reset-password-main-content ">
      <form
        class="reset-form"
        @submit.prevent="handleSubmit"
      >
        <h2 class="reset-form-title">
          Reset Your Password
        </h2>

        <div
          v-if="error"
          class="sh-form-field-error reset-form-banner"
          style="color:red"
        >{{ error }}</div>
        <div
          v-if="success"
          class="reset-form-banner"
          style="color:green"
        >{{ success }}</div>

        <div class="reset-form-group">
          <label
            for="reset-password"
            class="dg text-align-left"
          >Password</label>
          <div class="sh-password-input-container">
            <input
              id="reset-password"
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              name="password"
              autocomplete="new-password"
              class="sh-form-input sh-password-input"
              :class="{ 'input-error': errorMessages.password }"
              @click="scrollTop"
            >
            <button
              type="button"
              class="sh-toggle-password"
              :aria-label="showPassword ? 'Hide password' : 'Show password'"
              @click="togglePasswordVisibility('password')"
            >
              <i
                :class="showPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"
                aria-hidden="true"
              />
            </button>
          </div>
          <span
            v-show="errorMessages.password"
            class="sh-form-field-error"
          >{{ errorMessages.password }}</span>
        </div>

        <div class="reset-form-group">
          <label
            for="reset-password-confirm"
            class="dg text-align-left"
          >Confirm Password</label>
          <div class="sh-password-input-container">
            <input
              id="reset-password-confirm"
              v-model="passwordConfirmation"
              :type="showPasswordConfirmation ? 'text' : 'password'"
              name="passwordConfirmation"
              autocomplete="new-password"
              class="sh-form-input sh-password-input"
              :class="{ 'input-error': errorMessages.passwordConfirmation }"
              @click="scrollTop"
            >

            <button
              type="button"
              class="sh-toggle-password"
              :aria-label="showPasswordConfirmation ? 'Hide password' : 'Show password'"
              @click="togglePasswordVisibility('passwordConfirmation')"
            >
              <i
                :class="showPasswordConfirmation ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"
                aria-hidden="true"
              />
            </button>
          </div>

          <span
            v-show="errorMessages.passwordConfirmation"
            class="sh-form-field-error"
          >{{
            errorMessages.passwordConfirmation
          }}</span>
        </div>

        <button
          type="submit"
          class="sh-login-button reset-submit-button"
          :disabled="isSubmitting"
        >
          {{ isSubmitting ? 'Resetting...' : 'Reset Password' }}
        </button>
      </form>
    </main>

    <div class="sh-right-footer reset-form-footer">
      <a href="mailto:support@rallyhood.com">Contact Us</a>
      <span
        class="sh-footer-sep"
        aria-hidden="true"
      >&bull;</span>
      <a
        href="https://www.rallyhood.com/home/terms.php"
        target="_blank"
        rel="noopener"
      >Terms &amp; Conditions</a>
      <span
        class="sh-footer-sep"
        aria-hidden="true"
      >&bull;</span>
      <a
        href="https://www.rallyhood.com/home/privacy"
        target="_blank"
        rel="noopener"
      >Privacy Policy</a>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";

import { router } from "../common";
import { userInfo } from "@/common/user-info";

export default {
  emits: ["saveComplete"],
  data() {
    return {
      token: "",
      password: "",
      passwordConfirmation: "",
      showPassword: false,
      showPasswordConfirmation: false,
      isSubmitting: false,
      hasInteracted: { password: false, passwordConfirmation: false },
      errorMessages: { password: '', passwordConfirmation: '' },
      error: '',
      success: '',
    };
  },
  computed: {
    // REQUIRED: resetPassword swallows API failures and records them in
    // store state — without mapping `status`, this.status is undefined and
    // a failed reset reports success.
    ...mapState("user", ["status"]),
  },
  watch: {
    '$route.query.prt': {
      immediate: true,
      handler(val) {
        this.token = val || null
      }
    },
    password(val) {
      this.hasInteracted.password = true;
      this.validatePassword();
      if (this.hasInteracted.passwordConfirmation && this.passwordConfirmation) {
        this.validatePasswordConfirmation();
      }
    },
    passwordConfirmation(val) {
      this.hasInteracted.passwordConfirmation = true;
      this.validatePasswordConfirmation();
    }
  },
  created() {
    this.token = this.$route.query.prt || null;
    this.loadPageData();
  },
  methods: {
    ...mapActions("user", ["changeUserPassword", "resetPassword", "clearUsersState"]),
    validatePassword() {
      const pwd = this.password;
      if (!pwd) {
        this.errorMessages.password = 'Password is required.';
        return false;
      }
      // Mirrors the shared isPasswordValid rules (incl. lowercase) so this
      // page can't accept a password another flow will reject.
      if (pwd.length < 8 || !/[a-z]/.test(pwd) || !/[A-Z]/.test(pwd) || !/[0-9]/.test(pwd) || !/[^A-Za-z0-9]/.test(pwd)) {
        this.errorMessages.password = 'Your password must be at least 8 characters and contain one number, one special character, one capital letter, and one lowercase letter.';
        return false;
      }
      this.errorMessages.password = '';
      return true;
    },
    validatePasswordConfirmation() {
      if (!this.passwordConfirmation) {
        this.errorMessages.passwordConfirmation = 'Please confirm your password.';
        return false;
      }
      if (this.password !== this.passwordConfirmation) {
        this.errorMessages.passwordConfirmation = 'Passwords do not match.';
        return false;
      }
      this.errorMessages.passwordConfirmation = '';
      return true;
    },
    async handleSubmit() {
      this.hasInteracted.password = true;
      this.hasInteracted.passwordConfirmation = true;

      const isPasswordValid = this.validatePassword();
      const isConfirmValid = this.validatePasswordConfirmation();

      if (!isPasswordValid || !isConfirmValid) {
        return;
      }

      this.isSubmitting = true;
      this.error = '';
      this.success = '';

      // Everything renders in-page: window.alert() fired after an await is
      // unreliable in the iOS webview, and silent/false outcomes here were
      // a long-standing "dead reset screen" support mystery.
      try {
        if (this.token) {
          // Emailed prt values arrive as compound strings
          // ("<token>/1/<tracking>/<sig>"); RAS wants only the first
          // segment — same normalization the router's web guard applies.
          const token = String(this.token).split('/')[0]
          await this.resetPassword({ token: token, password: this.password });
          if (this.status?.error) {
            this.error = 'We could not reset your password — the link may have expired. Please request a new reset email.'
            this.clearUsersState()
          } else {
            // Let the confirmation actually be seen before redirecting —
            // an instant push reads as an unexplained jump to login.
            this.success = 'Password changed! Taking you to log in…'
            setTimeout(() => router.push("/login"), 1500)
          }
        } else {
          // No reset token: the deep link lost its prt param (e.g. cold
          // start). There is no logged-in user to change on this page.
          this.error = 'This reset link is missing its security code. Please open the link from your reset email again, or request a new one.'
        }
      } catch (e) {
        console.error('Reset password error:', e);
        this.error = 'Something went wrong saving your password. Please try again or request a new reset email.'
      } finally {
        this.isSubmitting = false;
      }
    },
    async loadPageData() {
      this.user_id = userInfo()?.user_id;
    },
    togglePasswordVisibility(field) {
      if (field === 'password') {
        this.showPassword = !this.showPassword
      } else if (field === 'passwordConfirmation') {
        this.showPasswordConfirmation = !this.showPasswordConfirmation
      }
    },
    cancel() {
      router.push("/login");
    },
    // Referenced by the inputs' @click; the copied loginform-ref version
    // targeted a ref this template doesn't have.
    scrollTop() {
      this.$el.scrollTop = 0
    }
  }
};
</script>
